// GeoDataPanel.js

import React, { useState, useEffect, Fragment, useRef } from "react"; 
import { connect } from "react-redux";
import { Spinner } from "react-bootstrap";
import axios from "axios"; // Ensure axios is imported
import MapGeoJsonService from "../../../services/MapGeoJsonService";
import GeoDataFicheProprietaire from "./geodata/GeoDataFicheProprietaire";
import GeoDataUrba from "./geodata/GeoDataUrba";
import GeoDataRisque from "./geodata/GeoDataRisque";
import GeoDataServitude from "./geodata/GeoDataServitude";
import UeDataBullet from "./geodata/UeDataBullet";
import UeDataBulletL from "./geodata/UeDataBulletL";
import GeoDataPreemption from "./geodata/GeoDataPreemption";
import GeoDataInformation from "./geodata/GeoDataInformation";
import GeoDataReseaux from "./geodata/GeoDataReseaux";

const GeoDataPanel = (props) => {
    const [parcelles, setParcelles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tauxInconstructible, setTauxInconstructible] = useState(null);
    const [tauxResiduel, setTauxResiduel] = useState(null);
    const [infoParcelle, setInfoParcelle] = useState({}); // Initialize as empty object
    const [geomInconstructible, setGeomInconstructible] = useState(null);
    const [geomResiduel, setGeomResiduel] = useState(null);
    const [isGeomLocked, setIsGeomLocked] = useState(false); // New state variable
    const [isResiduelLocked, setIsResiduelLocked] = useState(false);
    const [showSurface, setShowSurface] = useState(true);

    // Références pour les boutons
    const inconstructibleButtonRef = useRef(null);
    const sousConditionButtonRef = useRef(null);

    const displayGeomInconstructible = () => {
        if (!geomInconstructible || geomInconstructible.length === 0) {
            console.warn("No geometries to display for inconstructible areas.");
            return;
        }
        const mapGeoJsonService = new MapGeoJsonService();
        mapGeoJsonService.addInconstructibleLayer(geomInconstructible);
    };

    const hideGeomInconstructible = () => {
        const mapGeoJsonService = new MapGeoJsonService();
        mapGeoJsonService.clearInconstructibleLayer();
    };

    const displayGeomResiduel = () => {
        if (!geomResiduel || geomResiduel.length === 0) {
            console.warn("No geometries to display for residuel areas.");
            return;
        }
        const mapGeoJsonService = new MapGeoJsonService();
        mapGeoJsonService.addResiduelLayer(geomResiduel);
    };

    const hideGeomResiduel = () => {
        const mapGeoJsonService = new MapGeoJsonService();
        mapGeoJsonService.clearResiduelLayer();
    };

    const handleMouseEnter = () => {
        if (!isGeomLocked) {
            displayGeomInconstructible();
        }
    };

    const handleMouseLeave = () => {
        if (!isGeomLocked) {
            hideGeomInconstructible();
        }
    };

    const toggleGeomLock = () => {
        setIsGeomLocked((prev) => {
            const newState = !prev;
            if (newState) {
                displayGeomInconstructible();
            } else {
                hideGeomInconstructible();
                if (inconstructibleButtonRef.current) {
                    inconstructibleButtonRef.current.blur(); // Remove focus when unlocking
                }
            }
            return newState;
        });
    };

    const toggleResiduelLock = () => {
        setIsResiduelLocked((prev) => {
            const newState = !prev;
            if (newState) {
                displayGeomResiduel();
            } else {
                hideGeomResiduel();
                if (sousConditionButtonRef.current) {
                    sousConditionButtonRef.current.blur(); // Remove focus when unlocking
                }
            }
            return newState;
        });
    };

    const handleResiduelMouseEnter = () => {
        if (!isResiduelLocked) {
            displayGeomResiduel();
        }
    };

    const handleResiduelMouseLeave = () => {
        if (!isResiduelLocked) {
            hideGeomResiduel();
        }
    };

    const fetchData = () => {
        setIsLoading(true);

        // Validate currentParcelles
        if (props.currentParcelles.length === 0) {
            setIsLoading(false);
            return;
        }

        const params = {
            key_parcelles: props.currentParcelles,
        };

        axios
            .post('/Api/geodata/stat/parcelle/constructible', params)
            .then((result) => {
                const data = result.data;
                console.log("constructible", data);
                if (Array.isArray(data) && data.length > 0) {
                    // Calculate the sum of 'pourcentage_inconstructible'
                    const totalInconstruct = data.reduce((accumulator, item) => {
                        const value = parseFloat(item.pourcentage_inconstructible);
                        return accumulator + (isNaN(value) ? 0 : value);
                    }, 0);

                    // Calculate the average
                    const averageInconstruct = totalInconstruct / data.length;

                    // Calculate the sum of 'pourcentage_residuel'
                    const totalResiduel = data.reduce((accumulator, item) => {
                        const value = parseFloat(item.pourcentage_residuel);
                        return accumulator + (isNaN(value) ? 0 : value);
                    }, 0);

                    // Calculate the average
                    const averageResiduel = totalResiduel / data.length;

                    // Set the average with two decimal precision
                    setTauxInconstructible(Number(averageInconstruct.toFixed(2)));
                    setTauxResiduel(Number(averageResiduel.toFixed(2)));

                    setGeomInconstructible(data[0].combined_geom_inconstructible);
                    setGeomResiduel(data[0].combined_geom_residuel);

                } else {
                    // If data is empty or not an array, set to 0 or handle as needed
                    setTauxInconstructible(0);
                    setTauxResiduel(0); // Ensure both are reset
                }

                setIsLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching data:', err);
                setIsLoading(false);
            });
    };

    const fetchInfoParcelle = () => {
        setIsLoading(true);

        // Validate currentParcelles
        if (props.currentParcelles.length === 0) {
            setIsLoading(false);
            return;
        }

        const params = {
            key_parcelles: props.currentParcelles,
        };

        axios
            .post('/Api/geodata/parcelle/detail', params)
            .then((result) => {
                const data = result.data;
                // Check if data is an object and has at least one key
                if (data && Object.keys(data).length > 0) {
                    setInfoParcelle(data);
                } else {
                    setInfoParcelle({});
                }

                setIsLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching parcelle details:', err);
                setInfoParcelle({});
                setIsLoading(false);
            });
    };
    
    useEffect(() => {
        setIsLoading(true);
        props.subscription ? props.setTotalCoundDataRef(14) : props.setTotalCoundDataRef(2);
        if (props?.entity?.genre?.slug === 'professionnel' && props?.subscription?.plan?.nickname === 'abo-pro'){
            setShowSurface(false);
        }
    }, []);

    const fetchDataChain = async () => {
        await fetchInfoParcelle();
        fetchData();
    };
    
    useEffect(() => {
        hideGeomInconstructible();
        hideGeomResiduel(); // Ajout pour réinitialiser Residuel
        setIsGeomLocked(false);
        setIsResiduelLocked(false); // Réinitialiser l'état Residuel
        props.resetListDataRef(null);
        setTauxInconstructible(null);
        setTauxResiduel(null); // Réinitialiser également le taux résiduel
        setParcelles(props.currentParcelles);
        fetchDataChain();
    }, [props.currentParcelles]);

    // Gestionnaires de focus
    useEffect(() => {
        if (isGeomLocked && inconstructibleButtonRef.current) {
            inconstructibleButtonRef.current.focus();
        }
    }, [isGeomLocked]);

    useEffect(() => {
        if (isResiduelLocked && sousConditionButtonRef.current) {
            sousConditionButtonRef.current.focus();
        }
    }, [isResiduelLocked]);

    return (
        <div id="ueGeodataPanel">
            <div className="d-flex flex-row justify-content-center">
                <div className="mb-1">
                    {tauxInconstructible !== null && tauxInconstructible !== undefined ? (
                        <div>
                            <div className="flex items-center w-100 text-center label-color mb-3 ue-table-data inconstructible-label">
                                Cliquez ici pour afficher les surfaces
                            </div>
                            <div className="btn-group w-100 justify-content-between">
                                {/* Bouton Inconstructible */}
                                <button
                                    ref={inconstructibleButtonRef} // Attachez la ref
                                    className={`btn btn-sm mr-2 ${isGeomLocked ? 'btn-active' : ''} custom-focus`}
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    data-trigger="hover"
                                    title="Inconstructible"
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={toggleGeomLock}
                                    style={{
                                        /* Ne pas modifier la couleur de fond */
                                        height: "38px",
                                        width: "200px",
                                        display: "flex",
                                        alignItems: "center",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        outline: "none", // Laissez le CSS gérer le focus
                                        backgroundColor: "#70979A", // Couleur de fond fixe
                                    }}
                                    aria-pressed={isGeomLocked} // Accessibilité
                                >
                                    <UeDataBulletL color={"var(--theme-danger)"} />
                                    <span
                                        className="text-center"
                                        style={{
                                            paddingLeft: "7px",
                                            fontWeight: "700",
                                        }}
                                    >
                                        Inconstructible <span className="ue-geodata-inconstr-rate">{tauxInconstructible}%</span>
                                    </span>
                                </button>

                                {/* Bouton Sous Condition */}
                                <button
                                    ref={sousConditionButtonRef} // Attachez la ref
                                    className={`btn btn-sm ${isResiduelLocked ? 'btn-active' : ''} custom-focus`}
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    data-trigger="hover"
                                    title="Sous Condition"
                                    onMouseEnter={handleResiduelMouseEnter}
                                    onMouseLeave={handleResiduelMouseLeave}
                                    onClick={toggleResiduelLock}
                                    style={{
                                        /* Ne pas modifier la couleur de fond */
                                        height: "38px",
                                        width: "200px",
                                        display: "flex",
                                        alignItems: "center",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        outline: "none", // Laissez le CSS gérer le focus
                                        backgroundColor: "#70979A", // Couleur de fond fixe
                                    }}
                                    aria-pressed={isResiduelLocked} // Accessibilité
                                >
                                    <span
                                        className="text-center"
                                        style={{
                                            paddingLeft: "7px",
                                            fontWeight: "700",
                                        }}
                                    >
                                        <span className="ue-geodata-constr-rate">{tauxResiduel}%</span> Sous Condition
                                    </span>
                                    <UeDataBullet color={"var(--theme-secondary)"} />
                                </button>
                            </div>
                        </div>
                    ) : isLoading ? (
                        <div className="text-center my-2">
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center" }}>
                             {props.subscription ? (<>
                                <UeDataBullet color={"var(--theme-danger)"} /> Inconstructible
                                <UeDataBullet color={"var(--theme-secondary)"} /> Sous Condition
                             </>):(<></>)}
                        </div>
                    )}
                </div>
            </div>
            <div style={{ userSelect: "none" }}>
                {parcelles.length > 0 ? (
                    <Fragment>
                        {props.subscription ||
                            (props.entity && props.entity.genre && props.entity.genre.slug === "collective") ? (
                            <div style={{ paddingBottom: "30px" }}>
                                <GeoDataFicheProprietaire keypars={parcelles} />
                                {!isLoading && parcelles.length > 0 && Object.keys(infoParcelle).length > 0 ? (
                                    <Fragment>
                                        <GeoDataUrba 
                                            keypars={parcelles} 
                                            showSurface = {showSurface}
                                            infoParcelle={infoParcelle?.["urbanisme"] || []} 
                                            infoParcellePresc={infoParcelle?.["prescription"] || []}
                                            infoTaxe={infoParcelle?.["taxe"] || []} 
                                        />
                                        <GeoDataRisque 
                                            keypars={parcelles}
                                            showSurface = {showSurface}
                                            infoParcelle={infoParcelle?.["risque"] || []} 
                                        />
                                        <GeoDataServitude 
                                            keypars={parcelles}
                                            showSurface = {showSurface}
                                            infoParcelle={infoParcelle?.["servitude"] || []} 
                                            perimTerritoriaux={infoParcelle?.["perimetres_territoriaux"] || []} 
                                        />
                                        <GeoDataPreemption 
                                            keypars={parcelles}
                                            showSurface = {showSurface}
                                            infoParcelle={infoParcelle?.["preemption"] || []} 
                                        />
                                        <GeoDataInformation 
                                            keypars={parcelles}
                                            showSurface = {showSurface}
                                            infoParcelle={infoParcelle?.["perimetre"] || []} 
                                        />
                                    </Fragment>
                                ) : isLoading ? (
                                    <div className="text-center my-2">
                                        <Spinner animation="border" size="sm" />
                                    </div>
                                ) : null}

                                {/* Other components without specific sections */}
                                {/* <GeoDataPrescription keypars={parcelles} /> */}
                                {showSurface &&(
                                    <GeoDataReseaux keypars={parcelles} />
                                )}
                            </div>
                        ) : (
                            <div>
                                {/* Non-subscribed view */}
                                
                                <div onClick={() => props.setShowPricing(true)}>
                                    <img
                                    src="/img/left_panel_blurred_placeholder.png"
                                    alt="Réservé aux abonnés"
                                    style={{ width: "100%" }}
                                  />
                                </div>
                            </div>
                        )}
                    </Fragment>
                ) : isLoading ? (
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                ) : null}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        currentParcelles: state.MapReducer.currentParcelle,
        subscription: state.MeReducer.subscription,
        entity: state.MeReducer.entity,
        refMap: state.MapReducer.refMap,
    };
};

const mapDispatchTProps = (dispatch) => {
    return {
        setTotalCoundDataRef: (e) => dispatch({ type: "SET_COUNT_TOTAL-DATA_REF", data: e }),
        resetListDataRef: () => dispatch({ type: "RESET_LIST_DATA_REF" }),
        setShowPricing: (e) => dispatch({ type: "SET_SHOW_PRICING", data: e }),
    };
};

export default connect(mapStateToProps, mapDispatchTProps)(GeoDataPanel);
